import { Tooltip } from 'antd'
import warningIcon from 'assets/icons/warning.svg'
import { Show } from 'common/components/Show/Show'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import { UtilService } from 'common/services/utilService'
import { format, parse } from 'date-fns'
import dayjs from 'dayjs'
import { SMSIcon } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/SMSIcon/SMSIcon'
import {
  INFO_CONSTANTS,
  PATIENT_STATUSES,
  SMS_STATUSES,
} from 'features/Home/constants/infoConstants'
import { JitsiTelehealthCallButton } from 'features/JitsiTelehealth'
import { ROOM_TYPE_IDS } from 'features/Home/constants/room.constants'
import type { AppointmentCategories } from 'features/Schedule/api/schedule.types'
import type { AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import type { StatusColorName } from 'features/SettingsOld/state/api/types'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import './scheduleCalendar.scss'
import { EhrSyncIcon } from './EhrSyncIcon'
import { getAppointmentColor } from './helpers'
import { useScheduleEvent } from './scheduleEventStore'

type CalendarEventProps = {
  event: any
  appointmentColor: AppointmentColorType
  appointmentCategories: AppointmentCategories[]
  patientStatuses: StatusColorName[]
  isWeekView?: boolean
  hasEhrBooking?: boolean
  showConsultationStaffInitials?: boolean
}

const resolveConsultationStaffInitials = (consultationStaffName?: string): string => {
  const normalizedName = consultationStaffName?.trim() || ''

  if (!normalizedName) {
    return ''
  }

  const nameParts = normalizedName.replace(',', ' ').split(/\s+/).filter(Boolean)
  const firstName = nameParts[0] || ''
  const lastName = nameParts[nameParts.length - 1] || ''
  const initials = UtilService.getInitials(firstName, lastName)

  if (initials) {
    return initials
  }

  if (firstName) {
    return firstName.slice(0, 2).toUpperCase()
  }

  return ''
}

const CalendarEvent: FC<CalendarEventProps> = ({
  event: eventProp,
  appointmentColor,
  appointmentCategories,
  patientStatuses,
  isWeekView = false,
  hasEhrBooking = false,
  showConsultationStaffInitials = false,
}) => {
  const event = useScheduleEvent(eventProp?.id, eventProp)

  const { color: statusFillColor, borderColor: categoryColor } = getAppointmentColor({
    appointmentColor,
    categoryStatuses: appointmentCategories,
    statusColorName: patientStatuses,
    statusCode: event?.status?.code,
    categoryCode: event?.category?.code,
  })
  const statusColor = appointmentColor === 'status' ? statusFillColor : categoryColor

  const [previousLocation, setPreviousLocation] = useState<string | null>(null)

  const appointmentOriginalTime = useMemo(() => {
    if (event.room_type_id !== ROOM_TYPE_IDS.WAITING_ROOM) return
    const { appointment_start_time, appointment_end_time, original_room } = event
    if (!appointment_start_time || !appointment_end_time) return ''
    const startDate = parse(appointment_start_time, 'HH:mm:ss', new Date())
    const endDate = parse(appointment_end_time, 'HH:mm:ss', new Date())

    const start = format(startDate, TIME_FORMAT.TIME_GUTTER_PICKER_HH_SS)
    const end = format(endDate, TIME_FORMAT.TIME_GUTTER_PICKER_HH_SS)
    return `Original appointment: (${start} - ${end}, ${original_room})`
  }, [event])

  useEffect(() => {
    if (event.current_location) {
      setPreviousLocation(event.current_location)
    }
  }, [event.current_location])

  const startTime = useMemo(() => {
    return dayjs(event.start_time).format('h:mm')
  }, [event])

  const endTime = useMemo(() => {
    return dayjs(event.end_time).format('h:mm A')
  }, [event])

  const fullTitle = useMemo(() => {
    return startTime + ' - ' + endTime + ' | ' + event.patient.name
  }, [event])

  const statusCode = event?.status?.code

  const showLocationCategories = [
    PATIENT_STATUSES.CHECKED_IN_ON_SITE,
    PATIENT_STATUSES.IN_EXAM_ROOM,
    PATIENT_STATUSES.OUT_OF_EXAM_ROOM,
  ]

  const hasNotEhrSync = hasEhrBooking && !event.emr_updated_at
  const hasConsultationStaffName = Boolean(event.consultation_staff_name?.trim())
  const consultationStaffInitials = useMemo(() => {
    return resolveConsultationStaffInitials(event.consultation_staff_name)
  }, [event.consultation_staff_name])

  return (
    <div className='template-wrap'>
      <div className='subject'>
        <div className='event-name-container'>
          <div className='truncate-text'>
            <Tooltip
              title={
                <div>
                  {fullTitle}

                  {(event.comments || event.notes) && (
                    <>
                      <br />
                      <br />
                      {event.comments || event.notes}
                    </>
                  )}
                  {hasNotEhrSync && (
                    <>
                      <br />
                      <div className='flex'>
                        <EhrSyncIcon showIcon hideTooltip />
                        <span style={{ marginLeft: '4px' }}>Appointment not synced with EHR</span>
                      </div>
                    </>
                  )}
                </div>
              }
              placement='topLeft'>
              <span className='event-name-with-staff'>
                <JitsiTelehealthCallButton schedule={event} variant='icon' />
                {showConsultationStaffInitials &&
                  hasConsultationStaffName &&
                  consultationStaffInitials && (
                    <span className='event-consultation-staff-initials'>
                      {consultationStaffInitials}
                    </span>
                  )}
                <span className='event-name-text'>
                  {startTime} - {event.patient.name}
                </span>
              </span>
            </Tooltip>
            <EhrSyncIcon showIcon={hasNotEhrSync} className='ehr-sync-container' />
          </div>
        </div>

        <Show
          when={
            event.sms_notification !== SMS_STATUSES.UNCONFIRMED &&
            statusCode !== PATIENT_STATUSES.CHECK_IN_REJECTED
          }>
          <div className='event-sms'>
            <SMSIcon notificationTitle={event.notification_title} status={event.sms_notification} />
          </div>
        </Show>
        <Show when={statusCode === PATIENT_STATUSES.CHECK_IN_REJECTED}>
          <div className='event-sms'>
            <img src={warningIcon} alt={ALT_CONSTANTS.STATUS_ICON} />
          </div>
        </Show>
      </div>
      <div className='time'>
        <span>
          <Show when={appointmentOriginalTime}>
            <p style={{ lineHeight: '150%' }} className='rbc-event-label truncate-text'>
              {appointmentOriginalTime}
            </p>
          </Show>
        </span>
      </div>
      <Show
        when={![PATIENT_STATUSES.CHECKED_IN_ON_SITE, PATIENT_STATUSES.NONE].includes(statusCode)}
        fallback={
          <div
            style={{
              color: statusColor,
              whiteSpace: 'initial',
            }}
            className='event-description'>
            {event.comments}
          </div>
        }>
        <div className='event-description' style={{ color: statusColor }}>
          {event.status.name}
        </div>
      </Show>
      <Show when={showLocationCategories.includes(statusCode) && !isWeekView}>
        <p className='event-status'>
          <span>{INFO_CONSTANTS.NOW_IN} </span>
          {event.current_location
            ? event.current_location
            : previousLocation
            ? previousLocation
            : 'N/A'}
        </p>
      </Show>
      <Show
        when={
          statusCode === PATIENT_STATUSES.CHECK_IN_REJECTED ||
          statusCode === PATIENT_STATUSES.CHECKED_OUT
        }>
        <p className='event-status'>
          <span>{INFO_CONSTANTS.REASON}</span>
          {event.notes || 'N/A'}
          <Show when={event.temperature && statusCode !== PATIENT_STATUSES.CHECKED_OUT}>
            <span className='event-temperature'>{` ${event.temperature}°F`}</span>
          </Show>
        </p>
      </Show>
    </div>
  )
}

export default CalendarEvent
